import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import WorkAgreement from "work/work-agreements/work-agreement";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { Masked } from "work/_shared/masked-fields";

const ListContainer = styled(Grid)(
  () => ({
    flexWrap: "nowrap",
    minHeight: "20rem",
    width: "100%",
  }));
const DocumentList = styled(DocumentListReadonly)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    maxHeight: "60vh",
    marginLeft: theme.spacing(2),
    overflowY: "auto",
  }));
const HelperText = styled(Typography)(
  ({ theme }) => ({
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  }));

type PoliciesTabProps = {
  workAgreement: WorkAgreement;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const {
    workAgreement
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const policyDocument = await documentApiService.downloadDocument(
        documentId
      );
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Failed to download the document. Please try again later.",
        {
          variant: "error",
        }
      );
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (Masked.isMasked(workAgreement.vendorPolicies) ||
    Masked.isMasked(workAgreement.clientPolicies)) {
    return "You do not have permission to view the policy documents.";
  }

  const selectedDocuments = session.context?.viewingAsVendor
    ? workAgreement.vendorPolicies
    : workAgreement.clientPolicies;

  return (
    <ListContainer container direction="row">
      <Grid size={4}>
        {selectedDocuments.length === 0 &&
          <HelperText>
            No policy documents for this work agreement.
          </HelperText>
        }{selectedDocuments.length > 0 &&
          <DocumentList
            documents={selectedDocuments}
            documentType={
              session.context?.viewingAsVendor
                ? WorkDocumentType.VendorPolicy
                : WorkDocumentType.ClientPolicy
            }
            downloadingFileId={isDownloadingFile}
            onDownload={handleDownloadDocumentById}
          />
        }
      </Grid>
    </ListContainer>
  );
}
